import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { format as formatDate, formatDistanceToNow } from "date-fns";

import { APP_LOCALES, resolveLocale, type AppLocale } from "@/i18n/locales";

/**
 * Locale-aware view of the active language: direction flags plus formatters
 * that follow it.
 *
 * Use these instead of raw `date-fns`/`toLocaleString` so switching to Arabic
 * also localizes weekday names, month names and digit grouping — not just the
 * static UI copy.
 */
export const useLocale = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  return useMemo(() => {
    const locale: AppLocale = resolveLocale(language);
    const isRtl = locale.dir === "rtl";

    return {
      locale,
      /** Normalized code — `ar` for `ar-AE`. */
      code: locale.code,
      dir: locale.dir,
      isRtl,
      availableLocales: APP_LOCALES,
      changeLanguage: (code: string) => i18n.changeLanguage(code),

      /** `date-fns` format with the active locale's month/weekday names. */
      formatDate: (date: Date | number, pattern = "PP") =>
        formatDate(date, pattern, { locale: locale.dateFnsLocale }),

      /**
       * A span of days the way the locale writes it — "Oct 20 – 23, 2026" /
       * "20–23 أكتوبر 2026" — or one date when both fall on the same day.
       */
      formatDateRange: (
        start: Date | number,
        end: Date | number,
        options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" },
      ) => new Intl.DateTimeFormat(locale.code, options).formatRange(start, end),

      /** "3 days ago" / "قبل ٣ أيام". */
      formatRelative: (date: Date | number) =>
        formatDistanceToNow(date, {
          addSuffix: true,
          locale: locale.dateFnsLocale,
        }),

      formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
        new Intl.NumberFormat(locale.code, options).format(value),

      // `Intl.NumberFormat` throws a RangeError synchronously for anything
      // that isn't a valid ISO 4217 code — and nothing upstream catches it,
      // so a single malformed `currency` value (bad seed data, a stale
      // record) White-screens whatever page renders it. Degrade instead:
      // fall back to a plain number with the raw code appended.
      formatCurrency: (value: number, currency: string) => {
        try {
          return new Intl.NumberFormat(locale.code, {
            style: "currency",
            currency,
          }).format(value);
        } catch {
          return `${new Intl.NumberFormat(locale.code).format(value)} ${currency}`;
        }
      },

      /**
       * Picks between two values by direction — for the handful of cases that
       * genuinely cannot be expressed with CSS logical properties (e.g. a
       * Radix `side` prop, or an icon that must point "forward").
       */
      byDir: <T>(ltr: T, rtl: T): T => (isRtl ? rtl : ltr),
    };
  }, [language, i18n]);
};
